import React, { useMemo, useState } from "react";
import { MONTHLY_REVENUE, BRANCHES, KPI_SNAPSHOT } from "../lib/data";
import { CASHFLOW_13W, LEAD_TIME_ANALYSIS, WC_SCORECARD, AR_AGING, GM_WALK } from "../lib/dataExtras";
import { AP_OPTIMIZATION, AP_SUMMARY } from "../lib/dataUseCases";
import { HISTORICAL_KPI } from "../lib/dataHistorical";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import {
  BarChart, Bar, ComposedChart, Line, AreaChart, Area, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, LineChart, Cell, Legend,
} from "recharts";
import { useChartColors } from "../lib/chartColors";
import { useCopilot } from "../lib/copilot.jsx";
import { Sparkles, TrendingDown, TrendingUp, AlertCircle, MessageSquare } from "lucide-react";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value, p.dataKey) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

const PL = [
  { label: "Gross Revenue", value: 142_400_000, isHeader: true },
  { label: "  Physical Retail", value: 92_400_000, indent: true },
  { label: "  Shopee", value: 24_200_000, indent: true },
  { label: "  Direct / Corporate", value: 25_800_000, indent: true },
  { label: "Less: COGS", value: -97_120_000 },
  { label: "Gross Profit", value: 45_280_000, isHeader: true },
  { label: "  Gross Margin %", value: "31.8%", indent: true, isText: true },
  { label: "Less: Operating Expenses", value: -28_640_000 },
  { label: "  Branch OpEx", value: -18_240_000, indent: true },
  { label: "  Logistics & Warehousing", value: -4_820_000, indent: true },
  { label: "  Marketing & Promos", value: -3_180_000, indent: true },
  { label: "  HQ Overhead", value: -2_400_000, indent: true },
  { label: "EBITDA", value: 16_640_000, isHeader: true },
  { label: "  EBITDA Margin %", value: "11.7%", indent: true, isText: true },
];

export default function Financial() {
  const C = useChartColors();
  const { askCopilot } = useCopilot();
  const [activeSupplier, setActiveSupplier] = useState(LEAD_TIME_ANALYSIS[0]);

  const cashRisk = CASHFLOW_13W.filter(w => w.risk !== "ok").length;
  const lowestCash = Math.min(...CASHFLOW_13W.map(w => w.closing));
  const lowestWeek = CASHFLOW_13W.find(w => w.closing === lowestCash);

  // Build a waterfall-style series for the GM walk
  const gmWalkSeries = useMemo(() => {
    let running = GM_WALK[0].value;
    return GM_WALK.map((s, i) => {
      if (s.kind === "anchor") return { ...s, base: 0, bar: s.value, running: s.value };
      const base = s.value >= 0 ? running : running + s.value;
      const bar = Math.abs(s.value);
      running += s.value;
      return { ...s, base, bar, running };
    });
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART IV + IX · Stream 5" title="Financial Performance"
        description="13-week rolling cashflow projection, purchasing lead-time analysis, working capital scorecard and AR aging. CFO command view." />

      <div className="tile grid grid-cols-2 md:grid-cols-6 divide-x divide-border">
        <Agg label="Revenue MTD" value={formatPeso(KPI_SNAPSHOT.totalRevenueMTD, { compact: true })} status="success" />
        <Agg label="Gross Margin" value="31.8%" status="success" />
        <Agg label="EBITDA Margin" value="11.7%" status="success" />
        <Agg label="Cash on Hand" value={formatPeso(CASHFLOW_13W[0].opening, { compact: true })} status="success" />
        <Agg label="CCC (days)" value={WC_SCORECARD.ccc.toFixed(1)} status={WC_SCORECARD.ccc > WC_SCORECARD.cccTarget ? "warning" : "success"} />
        <Agg label="AR > 60d" value={formatPeso(AR_AGING.buckets[2].amount + AR_AGING.buckets[3].amount, { compact: true })} status="warning" />
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* 13-WEEK CASHFLOW PROJECTION */}
        <Tile className="col-span-12" testid="cashflow-13w"
              title="Intelligent 13-Week Rolling Cashflow Projection"
              action={<div className="flex items-center gap-2">
                {cashRisk > 0 && <Badge status="warning">{cashRisk} weeks below safety</Badge>}
                <Badge status="info">AI-Generated</Badge>
              </div>}>
          <div style={{ height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={CASHFLOW_13W} margin={{ top: 10, right: 10, left: 8, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="week" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis yAxisId="left" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
                <YAxis yAxisId="right" orientation="right" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
                <Tooltip content={<TT formatter={v => formatPeso(v, { compact: true })} />} cursor={{ fill: C.surface2 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <ReferenceLine yAxisId="right" y={CASHFLOW_13W[0].minThreshold} stroke={C.critical} strokeDasharray="4 4" label={{ value: "Min cash threshold", fill: "rgb(var(--critical))", fontSize: 10, position: "insideTopRight" }} />
                <Bar yAxisId="left" dataKey="ar" name="AR Collections" fill={C.chart[1]} stackId="in" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                <Bar yAxisId="left" dataKey="ap" name="AP Disbursements" fill={C.critical} stackId="out" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                <Bar yAxisId="left" dataKey="payroll" name="Payroll" fill={C.chart[4]} stackId="out" isAnimationActive={false} />
                <Bar yAxisId="left" dataKey="opex" name="OpEx" fill={C.chart[3]} stackId="out" isAnimationActive={false} />
                <Bar yAxisId="left" dataKey="capex" name="CapEx" fill={C.warning} stackId="out" isAnimationActive={false} />
                <Line yAxisId="right" type="monotone" dataKey="closing" name="Closing Cash" stroke={C.primary} strokeWidth={2.5} dot={{ r: 3, fill: C.primary }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-12 gap-3 mt-3 text-[11px]">
            <div className="col-span-12 md:col-span-8 p-3 bg-surface-2 border border-border rounded-sm">
              <div className="flex items-start gap-2.5">
                <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
                <div className="text-fg/90 leading-relaxed flex-1">
                  <strong className="text-primary">AI Treasury Commentary:</strong> Closing cash dips to {formatPeso(lowestCash, { compact: true })} in <strong>{lowestWeek.week}</strong> (driven by {lowestWeek.week === "W4" ? "LC settlements for Q1 Capella & Sanden imports" : lowestWeek.week === "W9" ? "Honeywell & Daikin LC settlements + summer pre-position" : "scheduled CapEx + payroll cycle"}). Net 13-week cash {CASHFLOW_13W[12].closing > CASHFLOW_13W[0].opening ? <span className="text-success">+{formatPeso(CASHFLOW_13W[12].closing - CASHFLOW_13W[0].opening, { compact: true })}</span> : <span className="text-critical">{formatPeso(CASHFLOW_13W[12].closing - CASHFLOW_13W[0].opening, { compact: true })}</span>}. Recommend: (1) accelerate AR follow-up on top-5 accounts in 60+ day bucket ({formatPeso(AR_AGING.topRisk.reduce((s, t) => s + t.amount, 0), { compact: true })}); (2) negotiate Net-75 on Capella PO #2241; (3) draw down ₱30M revolving credit by W6 as cushion.
                  <button
                    data-testid="ask-copilot-treasury"
                    onClick={() => askCopilot(
                      `As CFO, I'm reviewing the 13-week rolling cashflow. The closing cash trough is ${formatPeso(lowestCash, { compact: true })} in ${lowestWeek.week} (${lowestWeek.weekLabel}). Opening cash ${formatPeso(CASHFLOW_13W[0].opening, { compact: true })}; closing ${formatPeso(CASHFLOW_13W[12].closing, { compact: true })}; min-cash threshold ${formatPeso(CASHFLOW_13W[0].minThreshold, { compact: true })}; ${CASHFLOW_13W.filter(w => w.risk !== "ok").length} weeks below safety. AR > 60d totals ${formatPeso(AR_AGING.buckets[2].amount + AR_AGING.buckets[3].amount, { compact: true })}, top-5 at-risk customers sum ${formatPeso(AR_AGING.topRisk.reduce((s, t) => s + t.amount, 0), { compact: true })}. CCC is ${WC_SCORECARD.ccc.toFixed(1)} days (target ${WC_SCORECARD.cccTarget}d). Give me a prioritised treasury action plan: which levers (AR collections, supplier-term renegotiation, revolving credit draw-down, deferred CapEx) should I pull in what order and by which week to stay above the min-cash line, and quantify the impact of each lever?`
                    )}
                    className="ml-2 inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 hover:underline font-medium align-baseline whitespace-nowrap"
                  >
                    <MessageSquare size={11} /> Ask Copilot to deep-dive →
                  </button>
                </div>
              </div>
            </div>
            <div className="col-span-6 md:col-span-2 p-3 bg-surface-2 border border-border rounded-sm">
              <div className="kpi-label">Net Inflow (13w)</div>
              <div className={cn("kpi-value text-xl mt-1", CASHFLOW_13W.reduce((s, w) => s + w.netFlow, 0) >= 0 ? "text-success" : "text-critical")}>
                {formatPeso(CASHFLOW_13W.reduce((s, w) => s + w.netFlow, 0), { compact: true })}
              </div>
            </div>
            <div className="col-span-6 md:col-span-2 p-3 bg-surface-2 border border-border rounded-sm">
              <div className="kpi-label">Min Cash Week</div>
              <div className="kpi-value text-xl mt-1 text-warning">{lowestWeek.week}</div>
              <div className="text-[10px] text-muted font-mono-tabular">{lowestWeek.weekLabel}</div>
            </div>
          </div>
        </Tile>

        {/* PURCHASING LEAD TIME ANALYSIS */}
        <Tile className="col-span-12 lg:col-span-8" testid="lead-time-analysis"
              title="Purchasing Lead Time Analysis — Planned vs Actual"
              action={<Badge status="warning">{LEAD_TIME_ANALYSIS.filter(l => l.avgDelay > 5).length} suppliers with delays</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-2 text-left">Supplier</th>
                <th className="py-2 px-2 text-right">Planned</th>
                <th className="py-2 px-2 text-right">Actual</th>
                <th className="py-2 px-2 text-right">Δ Delay</th>
                <th className="py-2 px-2 text-right">OTD</th>
                <th className="py-2 px-2 text-right">Open PO ₱</th>
                <th className="py-2 px-2 text-center">Trend (14 POs)</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {LEAD_TIME_ANALYSIS.map(l => (
                <tr key={l.supplierId} data-testid={`lead-time-${l.supplierId}`}
                    onClick={() => setActiveSupplier(l)}
                    className={cn("border-b border-border hover:bg-surface-2 cursor-pointer transition-colors", activeSupplier.supplierId === l.supplierId && "bg-primary/5")}>
                  <td className="py-2 px-2 font-sans">
                    <div className="font-medium">{l.supplier}</div>
                    <div className="text-[10px] text-muted">{l.country} · {l.category}</div>
                  </td>
                  <td className="py-2 px-2 text-right">{l.plannedLT}d</td>
                  <td className="py-2 px-2 text-right">{l.avgActualLT}d</td>
                  <td className={cn("py-2 px-2 text-right font-semibold", l.avgDelay > 10 ? "text-critical" : l.avgDelay > 3 ? "text-warning" : "text-success")}>
                    {l.avgDelay >= 0 ? "+" : ""}{l.avgDelay}d
                  </td>
                  <td className={cn("py-2 px-2 text-right", l.otd < 0.8 ? "text-critical" : l.otd < 0.9 ? "text-warning" : "text-success")}>
                    {(l.otd * 100).toFixed(0)}%
                  </td>
                  <td className="py-2 px-2 text-right">{formatPeso(l.totalValue, { compact: true })}</td>
                  <td className="py-2 px-2">
                    <MiniBars obs={l.observations} c={C} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* Lead-time delay attribution waterfall */}
        <Tile className="col-span-12 lg:col-span-4" testid="lead-time-attribution"
              title={`Delay Attribution — ${activeSupplier.supplier}`}
              action={<Badge status="info">+{activeSupplier.avgDelay}d avg</Badge>}>
          <div className="space-y-3 mt-1">
            {activeSupplier.attribution.map((a, i) => {
              const total = activeSupplier.attribution.reduce((s, x) => s + x.days, 0) || 1;
              return (
                <div key={a.factor}>
                  <div className="flex justify-between text-xs mb-1">
                    <span>{a.factor}</span>
                    <span className="font-mono-tabular">{a.days}d</span>
                  </div>
                  <div className="h-3 bg-surface-2 rounded-sm overflow-hidden">
                    <div className="h-full" style={{ width: `${(a.days / total) * 100}%`, background: `rgb(var(--chart-${i + 1}))` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <hr className="my-3 border-border" />
          <div className="text-[11px] text-muted leading-relaxed">
            <strong className="text-fg">Mitigation:</strong> Port-congestion is the #1 driver. Recommend pre-clearing customs via accredited broker (saves ~3d) and shifting 25% volume to {activeSupplier.country === "China" ? "Thailand-routed sourcing" : "secondary supplier on standby"}.
          </div>
        </Tile>

        {/* Working Capital scorecard */}
        <Tile className="col-span-12 lg:col-span-7" testid="wc-scorecard"
              title="Working Capital Scorecard — DIO · DSO · DPO · CCC">
          <div className="grid grid-cols-4 divide-x divide-border">
            <WCStat label="DIO (Inventory Days)" actual={WC_SCORECARD.dio} target={WC_SCORECARD.dioTarget} good="below" />
            <WCStat label="DSO (Receivable Days)" actual={WC_SCORECARD.dso} target={WC_SCORECARD.dsoTarget} good="below" />
            <WCStat label="DPO (Payable Days)" actual={WC_SCORECARD.dpo} target={WC_SCORECARD.dpoTarget} good="above" />
            <WCStat label="CCC (Cash Cycle)" actual={WC_SCORECARD.ccc} target={WC_SCORECARD.cccTarget} good="below" highlight />
          </div>
          <div style={{ height: 200 }} className="mt-4">
            <ResponsiveContainer>
              <LineChart data={WC_SCORECARD.trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} domain={[20, 65]} />
                <Tooltip content={<TT formatter={v => `${v}d`} />} />
                <Line type="monotone" dataKey="DIO" stroke={C.chart[0]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="DSO" stroke={C.chart[1]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="DPO" stroke={C.chart[2]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* AR Aging */}
        <Tile className="col-span-12 lg:col-span-5" testid="ar-aging" title="AR Aging — Concentration Risk">
          <div className="space-y-2">
            {AR_AGING.buckets.map(b => (
              <div key={b.bucket} className="flex items-center gap-3">
                <StatusDot status={b.status} />
                <div className="w-24 text-xs">{b.bucket}</div>
                <div className="flex-1 h-5 bg-surface-2 rounded-sm overflow-hidden">
                  <div className={cn("h-full",
                    b.status === "success" ? "bg-success" : b.status === "info" ? "bg-info" : b.status === "warning" ? "bg-warning" : "bg-critical")}
                    style={{ width: `${(b.amount / AR_AGING.buckets[0].amount) * 100}%`, opacity: 0.7 }} />
                </div>
                <div className="text-right w-24 font-mono-tabular text-sm font-semibold">{formatPeso(b.amount, { compact: true })}</div>
                <div className="text-[10px] text-muted w-12 text-right">{b.count} acct</div>
              </div>
            ))}
          </div>
          <hr className="my-3 border-border" />
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Top 5 At-Risk Accounts (60+ days)</div>
          <div className="space-y-1.5 text-xs">
            {AR_AGING.topRisk.map((t, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-border last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{t.customer}</div>
                  <div className="text-[10px] text-muted">{t.branch}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-tabular font-semibold">{formatPeso(t.amount, { compact: true })}</div>
                  <div className="text-[10px] text-critical">{t.days} days</div>
                </div>
              </div>
            ))}
          </div>
        </Tile>

        {/* Gross Margin Walk */}
        <Tile className="col-span-12 lg:col-span-7" testid="gm-walk" title="Gross Margin Walk — Prior → Current Period">
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={gmWalkSeries} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tick={{ ...tickStyle, fontSize: 9, fontFamily: "IBM Plex Sans" }} stroke={C.borderStrong} interval={0} angle={-22} dy={14} height={50} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} domain={[28, 36]} tickFormatter={v => `${v.toFixed(1)}%`} />
                <Tooltip content={<TT formatter={(v, k) => k === "bar" || k === "value" ? `${Math.abs(v).toFixed(2)}pp` : v} />} cursor={{ fill: C.surface2 }} />
                <Bar dataKey="base" stackId="w" fill="transparent" />
                <Bar dataKey="bar" stackId="w" radius={[2, 2, 0, 0]}>
                  {gmWalkSeries.map((s, i) => (
                    <Cell key={i} fill={s.kind === "anchor" ? C.primary : s.kind === "positive" ? C.success : C.critical} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* PL Summary */}
        <Tile className="col-span-12 lg:col-span-5" testid="pl-summary" title="P&L Summary — MTD">
          <table className="w-full text-sm">
            <tbody>
              {PL.map((row, i) => (
                <tr key={i} className={cn("border-b border-border last:border-b-0",
                  row.isHeader && "bg-surface-2 font-display font-semibold")}>
                  <td className={cn("py-2 px-3", row.indent && "text-muted")}>{row.label}</td>
                  <td className={cn("py-2 px-3 text-right font-mono-tabular", row.value < 0 && !row.isText && "text-critical", row.isHeader && "font-semibold")}>
                    {row.isText ? row.value : formatPeso(row.value, { compact: false })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* AP / Working Capital Optimization */}
        <Tile className="col-span-12" testid="ap-optimization"
              title="AP & Working Capital Optimization — AI Recommendations"
              action={<div className="flex items-center gap-2">
                <Badge status="success">{formatPeso(AP_SUMMARY.workingCapitalUnlock, { compact: true })} unlockable</Badge>
                <button
                  data-testid="ask-copilot-ap"
                  onClick={() => askCopilot(
                    `[AP Optimisation Deep-Dive] As CFO, I'm reviewing the AP optimization queue. Total open payables ${formatPeso(AP_SUMMARY.totalOpenPayables, { compact: true })}, AI projects ${formatPeso(AP_SUMMARY.workingCapitalUnlock, { compact: true })} working capital unlock if I execute the recommended actions. The supplier-level recommendations: ${AP_OPTIMIZATION.map(a => `${a.supplier} (${formatPeso(a.invoiceValue, { compact: true })}, current term: ${a.currentTerm}) → ${a.recommend.toUpperCase()}: ${a.action}, savings ${formatPeso(a.savings)}, risk score ${a.riskScore}, reason: ${a.reason}`).join("; ")}. Current DPO ${WC_SCORECARD.dpo}d, target ${AP_SUMMARY.recommendedDpoTarget}d. Walk me through the trade-offs: (1) which of these should I execute this week vs hold? (2) which suppliers might push back and how do I de-risk? (3) what's the all-in financial impact across CCC, gross margin, and supplier-relationship score?`
                  )}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                >
                  <MessageSquare size={11} /> Ask Copilot →
                </button>
              </div>}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            <ApStat label="Open Payables" value={formatPeso(AP_SUMMARY.totalOpenPayables, { compact: true })} />
            <ApStat label="Early Discount" value={formatPeso(AP_SUMMARY.earlyDiscountCapture, { compact: true })} status="success" />
            <ApStat label="Term Extension Cash" value={formatPeso(AP_SUMMARY.termExtensionCash, { compact: true })} status="success" />
            <ApStat label="Current DPO" value={`${AP_SUMMARY.currentDpo}d`} />
            <ApStat label="Target DPO" value={`${AP_SUMMARY.recommendedDpoTarget}d`} status="info" />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Supplier</th>
                <th className="py-2 px-3 text-right">Invoice</th>
                <th className="py-2 px-3 text-left">Term</th>
                <th className="py-2 px-3 text-left">AI Action</th>
                <th className="py-2 px-3 text-right">Savings</th>
                <th className="py-2 px-3 text-right">DPO Δ</th>
                <th className="py-2 px-3 text-right">Risk</th>
                <th className="py-2 px-3 text-center">Recommend</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {AP_OPTIMIZATION.map(a => (
                <tr key={a.supplierId} className="border-b border-border hover:bg-surface-2" data-testid={`ap-${a.supplierId}`}>
                  <td className="py-2 px-3 font-sans">
                    <div className="font-medium text-sm">{a.supplier}</div>
                    <div className="text-[10px] text-muted max-w-md">{a.reason}</div>
                  </td>
                  <td className="py-2 px-3 text-right">{formatPeso(a.invoiceValue, { compact: true })}</td>
                  <td className="py-2 px-3 text-muted font-sans">{a.currentTerm}</td>
                  <td className="py-2 px-3 text-fg font-sans">{a.action}</td>
                  <td className="py-2 px-3 text-right text-success font-semibold">{a.savings > 0 ? formatPeso(a.savings) : "—"}</td>
                  <td className={cn("py-2 px-3 text-right", a.dpoImpact > 0 ? "text-success" : a.dpoImpact < 0 ? "text-warning" : "text-muted")}>
                    {a.dpoImpact === 0 ? "—" : (a.dpoImpact > 0 ? "+" : "") + a.dpoImpact + "d"}
                  </td>
                  <td className={cn("py-2 px-3 text-right", a.riskScore < 0.2 ? "text-success" : a.riskScore < 0.4 ? "text-warning" : "text-critical")}>
                    {(a.riskScore * 100).toFixed(0)}
                  </td>
                  <td className="py-2 px-3 text-center font-sans">
                    <Badge status={a.recommend === "accept" ? "success" : a.recommend === "negotiate" ? "warning" : "neutral"}>{a.recommend}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* 4-Year Historical KPI Trend */}
        <Tile className="col-span-12" testid="historical-kpi" title="4-Year Performance — Revenue · Margin · Forecast Accuracy · Working Capital">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Year</th>
                <th className="py-2 px-3 text-right">Revenue</th>
                <th className="py-2 px-3 text-right">YoY</th>
                <th className="py-2 px-3 text-right">GM%</th>
                <th className="py-2 px-3 text-right">EBITDA%</th>
                <th className="py-2 px-3 text-right">Forecast Acc</th>
                <th className="py-2 px-3 text-right">OTD</th>
                <th className="py-2 px-3 text-right">Stockout%</th>
                <th className="py-2 px-3 text-right">Turnover</th>
                <th className="py-2 px-3 text-right">CCC (d)</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {HISTORICAL_KPI.map((k, i) => {
                const prev = i > 0 ? HISTORICAL_KPI[i - 1] : null;
                const yoy = prev ? (k.revenue - prev.revenue) / prev.revenue : null;
                return (
                  <tr key={k.year} className="border-b border-border" data-testid={`yr-${k.year}`}>
                    <td className="py-2 px-3 font-sans font-semibold">{k.year}</td>
                    <td className="py-2 px-3 text-right">{formatPeso(k.revenue, { compact: true })}</td>
                    <td className={cn("py-2 px-3 text-right font-semibold", yoy === null ? "text-muted" : "text-success")}>
                      {yoy === null ? "—" : `+${(yoy * 100).toFixed(1)}%`}
                    </td>
                    <td className="py-2 px-3 text-right">{(k.gm * 100).toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right">{(k.ebitda * 100).toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right text-info">{(k.forecastAcc * 100).toFixed(0)}%</td>
                    <td className="py-2 px-3 text-right">{(k.otd * 100).toFixed(0)}%</td>
                    <td className="py-2 px-3 text-right text-warning">{(k.stockoutPct * 100).toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right">{k.turnover}×</td>
                    <td className="py-2 px-3 text-right">{k.ccc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tile>
      </div>
    </div>
  );
}

function ApStat({ label, value, status }) {
  return (
    <div className="p-2.5 border border-border rounded-sm bg-surface-2/40">
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-base mt-1">{value}</div>
    </div>
  );
}

function MiniBars({ obs, c }) {
  const max = Math.max(...obs.map(o => o.actual));
  return (
    <div className="inline-flex items-end gap-[2px] h-7">
      {obs.map((o, i) => {
        const h = (o.actual / max) * 100;
        return <div key={i} title={`${o.po}: ${o.actual}d (plan ${o.planned}d, ${o.delayDays >= 0 ? "+" : ""}${o.delayDays}d)`}
                    className="w-1.5 rounded-sm" style={{
          height: `${h}%`,
          background: o.delayDays > 5 ? "rgb(var(--critical))" : o.delayDays > 0 ? "rgb(var(--warning))" : "rgb(var(--success))",
        }} />;
      })}
    </div>
  );
}

function WCStat({ label, actual, target, good, highlight }) {
  const onTrack = good === "below" ? actual <= target * 1.05 : actual >= target * 0.95;
  return (
    <div className={cn("p-4", highlight && "bg-primary/5")}>
      <div className="kpi-label">{label}</div>
      <div className={cn("kpi-value text-3xl mt-2", highlight && "text-primary")}>{actual.toFixed(1)}<span className="text-base text-muted ml-1">d</span></div>
      <div className="text-[10px] mt-1.5 font-mono-tabular flex items-center gap-1.5">
        <span className="text-muted">Target {target.toFixed(0)}d</span>
        <span className={cn("font-semibold", onTrack ? "text-success" : "text-warning")}>
          {onTrack ? "✓" : "▲"} {Math.abs(actual - target).toFixed(1)}d
        </span>
      </div>
    </div>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
